import { useState, useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import { useToast } from '@/hooks/use-toast'
import { addChartAtom, createStandardChartObject, ChartData as LocalChartData } from '../store/chartAtoms'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'
import { organizationService } from '../services/organizationService'
import { DataSource } from '@shared'

// This would ideally be dynamically fetched or defined in a shared location
const MOCK_SCHEMA: Record<string, { metrics: string[], dimensions: string[] }> = {
  'google-analytics': {
    metrics: ['activeUsers', 'screenPageViews', 'sessions', 'conversions'],
    dimensions: ['date', 'deviceCategory', 'country', 'pagePath'],
  },
  'firestore': {
    metrics: ['documentCount'],
    dimensions: ['collection', 'createdAt', 'updatedAt'],
  }
}

export const useManualChartCreator = (dataSources: DataSource[], onClose: () => void) => {
  const [, addChart] = useAtom(addChartAtom)
  const { toast } = useToast()

  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string | null>(null)
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar')
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null)
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<string>('last 30 days')
  
  const [preview, setPreview] = useState<Partial<LocalChartData> | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedDataSource = useMemo(() => {
    return dataSources.find(ds => ds.id === selectedDataSourceId) || null
  }, [selectedDataSourceId, dataSources])

  const availableOptions = useMemo(() => {
    if (!selectedDataSource) return { metrics: [], dimensions: [] }
    return MOCK_SCHEMA[selectedDataSource.type as keyof typeof MOCK_SCHEMA] || { metrics: [], dimensions: [] }
  }, [selectedDataSource])

  // Reset selections when data source changes
  useEffect(() => {
    setSelectedMetric(null)
    setSelectedDimension(null)
    setPreview(null)
    setError(null)
  }, [selectedDataSourceId])

  const canGeneratePreview = selectedDataSourceId && chartType && selectedMetric && selectedDimension

  const generatePreview = async () => {
    if (!canGeneratePreview) {
      setError("Please select a data source, chart type, metric, and dimension.")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const payload = {
        organizationId: organizationService.getCurrentOrganizationId(),
        intent: `Manual: ${selectedMetric} by ${selectedDimension} for ${dateRange}`,
        parsedIntent: {
          metrics: [selectedMetric],
          dimensions: [selectedDimension],
          dateRange: dateRange,
          chartType: chartType,
        },
        availableDataSources: [selectedDataSourceId],
      }
      
      const generateChartFunction = httpsCallable(functions, 'generateChartFromIntent')
      const result = await generateChartFunction(payload)
      const resultData = result.data as any

      if (resultData.success && resultData.chartData) {
        setPreview(resultData.chartData)
      } else {
        throw new Error(resultData.message || 'Failed to generate preview.')
      }
    } catch (e: any) {
      setError(e.message || 'An unknown error occurred.')
      setPreview(null)
    } finally {
      setIsLoading(false)
    }
  }

  const createChart = () => {
    if (!preview) {
      toast({ title: "Cannot Create Chart", description: "A valid preview must be generated first.", variant: "destructive" })
      return
    }

    const newChart = createStandardChartObject({
      ...preview,
      title: `${selectedMetric} by ${selectedDimension}`,
    })

    addChart(newChart)
    toast({ title: "Chart Created! 🎉", description: `"${newChart.title}" has been added.` })
    onClose()
  }

  return {
    selectedDataSourceId,
    setSelectedDataSourceId,
    chartType,
    setChartType,
    selectedMetric,
    setSelectedMetric,
    selectedDimension,
    setSelectedDimension,
    dateRange,
    setDateRange,
    availableOptions,
    generatePreview,
    createChart,
    preview,
    isLoading,
    error,
    canGeneratePreview,
    canCreateChart: !!preview && !isLoading && !error,
  }
} 